// src/pages/SignIn.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Link,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import { scheduleTokenRefresh } from "../../utils/auth";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
const SignIn = ({ setToken }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLegacyRedirect = () => {
    window.location.href = "https://precise-sync.s2c.io";
    // window.location.href = "https://precise-sync.s2c.io/osc/MyApP?akey=APP-2021-04-16-22-57-10-318358&bkey=80.82.69.67.73.83.73.79.78.64.65.68.77.73.78.46.67.79.77#";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/users/signin`,
        { email: email.trim(), password },
        { withCredentials: true }
      );

      if (data.token) {
        setToken(data.token);
        scheduleTokenRefresh(data.token, setToken);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("token", data.token);
        //         const decoded = jwtDecode(data.token);
        // const appUrl = decoded.appUrl;
        // window.location.replace(appUrl);
      }

      // if (data.token) {
      //   setToken(data.token);
      //   localStorage.setItem("user", JSON.stringify(data.user));
      //   localStorage.setItem("token", data.token);

      //   // Role-based redirection
      //   if (data.user.role === "admin") {
      //     toast.success("Signed in as Admin!");
      //     navigate("/");
      //   } else {
      //     // Redirect to other domain with token
      //     toast.success("Redirecting to your portal...");
      //     window.location.href = `https://prc.s2c.io/auth/callback?token=${data.token}`;
      //   }
      // }

      toast.success("Signed in successfully!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to sign in. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        background: "linear-gradient(145deg, #1e293b, #111827)",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: { xs: 4, sm: 6 },
          width: { xs: "90%", sm: "500px" },
          maxWidth: "500px",
          textAlign: "center",
          backgroundColor: "#1f2937",
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            color: "#00BFFF",
            fontWeight: "bold",
            mb: 4,
          }}
        >
          Welcome Back
        </Typography>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError("")}
            sx={{
              mb: 3,
              backgroundColor: "#7f1d1d",
              color: "#fecaca",
              "& .MuiAlert-icon": { color: "#fecaca" },
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
              disabled={isLoading}
              variant="outlined"
              InputLabelProps={{
                style: { color: "#ffffffff", fontSize: "1.1rem" },
              }}
              InputProps={{
                sx: {
                  color: "#ffffffff",
                  backgroundColor: "#374151",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6b7280",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d6c1c1ff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00BFFF",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px #374151 inset",
                    WebkitTextFillColor: "#ffffffff",
                  },
                },
              }}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoComplete="current-password"
              disabled={isLoading}
              variant="outlined"
              InputLabelProps={{
                style: { color: "#ffffffff", fontSize: "1.1rem" },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      disabled={isLoading}
                      sx={{ color: "#ffffffff" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  color: "#00BFFF",
                  backgroundColor: "#374151",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6b7280",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d6c1c1ff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00BFFF",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading}
              sx={{
                backgroundColor: "#00BFFF",
                "&:hover": { backgroundColor: "#d6c1c1ff" },
                color: "#111827",
                fontWeight: "bold",
                fontSize: "1rem",
                py: 1.5,
                position: "relative",
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: "#111827" }} />
              ) : (
                "SIGN IN"
              )}
            </Button>
            {/* <Button
              variant="outlined"
              fullWidth
              disabled={isLoading}
              onClick={handleLegacyRedirect}
              sx={{
                borderColor: "#00BFFF",
                color: "#00BFFF",
                fontWeight: "bold",
                fontSize: "0.95rem",
                py: 1.3,
                "&:hover": {
                  borderColor: "#d6c1c1ff",
                  color: "#d6c1c1ff",
                  backgroundColor: "rgba(214, 193, 193, 0.08)",
                },
              }}
            >
              SIGN IN TO LEGACY WEBSITE
            </Button> */}
          </Stack>
        </form>

        <Box sx={{ mt: 3 }}>
          <Link
            href="/forgot-password"
            sx={{
              color: "#00BFFF",
              textDecoration: "none",
              "&:hover": { color: "#d6c1c1ff" },
            }}
          >
            Forgot password?
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignIn;
